import React, { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import {
  getWeeklyGeneralCustomerOrderCounts,
  getMonthlyGeneralCustomerOrderCounts,
} from "../../slices/thunks";
import { createSelector } from "reselect";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import ReactApexChart from "react-apexcharts";
import downloadImage from "../../assets/images/svg/download.svg";
import {
  DASHBOARD_DATE_FILTER_PERIODS,
  getDashboardDateRange,
  shouldUseMonthlyEndpoint,
} from "./dateFilterUtils";
import { toChartItems, toChartLabel, toChartNumber } from "./dashboardChartUtils";

const formatCurrency = (value) =>
  toChartNumber(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const WeeklyTotalMemberPurchases = ({ dateFilter = null }) => {
  const dispatch = useDispatch();
  const dateFrom = useRef(null);
  const dateTo = useRef(null);
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const isGlobalDateFilter = Boolean(dateFilter);
  const selectDashboardData = createSelector(
    (state) => state.Dashboard,
    (dashboard) => dashboard
  );
  const { weeklyGeneralCustomerOrderCounts } = useSelector(
    useMemo(() => selectDashboardData, [])
  );
  const chart = useRef(null);
  const [series, setSeries] = useState([
    {
      name: "Ordered",
      type: "column",
      data: [],
      color: "#0AB39C",
    },
    {
      name: "Cancelled",
      type: "column",
      data: [],
      color: "#F06548",
    },
    {
      name: "BPA Ordered",
      type: "column",
      data: [],
      color: "#405189",
    },
    {
      name: "Unique BPA Members",
      type: "column",
      data: [],
      color: "#FFB302",
    },
    {
      name: "Total Price",
      type: "line",
      data: [],
      color: "#00E396",
    },
  ]);

  const onDownload = () => {
    const data = {
      dateFrom:
        dateFrom.current != null
          ? moment(dateFrom.current).format("YYYY-MM-DD")
          : null,
      dateTo:
        dateTo.current != null
          ? moment(dateTo.current).format("YYYY-MM-DD")
          : null,
      download: true,
    };
    if (isGlobalDateFilter && shouldUseMonthlyEndpoint(dateFilter)) {
      dispatch(getMonthlyGeneralCustomerOrderCounts(data));
    } else {
      dispatch(getWeeklyGeneralCustomerOrderCounts(data));
    }
  };

  const [options, setOptions] = useState({
    chart: {
      height: 350,
      type: "line",
      stacked: false,
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true | '<img src="/static/icons/reset.png" width="20">',
          customIcons: [
            {
              icon: `<img src="${downloadImage}"  />`,
              title: "Download details",
              click: function () {
                onDownload();
              },
            },
          ],
        },
      },
    },
    stroke: {
      width: [0, 0, 0, 0, 3],
      curve: "smooth",
    },
    plotOptions: {
      bar: {
        columnWidth: "45%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: [],
      labels: {
        formatter: function (val) {
          return val;
        },
      },
      axisBorder: {
        show: true,
        color: "#e0e0e0",
      },
      axisTicks: {
        show: true,
        color: "#e0e0e0",
      },
      crosshairs: {
        fill: {
          type: "gradient",
          gradient: {
            colorFrom: "#D8E3F0",
            colorTo: "#BED1E6",
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
      },
      tooltip: {
        enabled: true,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (value, { seriesIndex }) {
          if (seriesIndex === 4) {
            return formatCurrency(value);
          }

          return value;
        },
      },
    },
    yaxis: [
      {
        seriesName: "Ordered",
        forceNiceScale: true,
        decimalsInFloat: 0,
        title: {
          text: "Counts",
        },
        labels: {
          formatter: function (value) {
            return Math.round(toChartNumber(value)).toString();
          },
        },
      },
      {
        seriesName: "Cancelled",
        show: false,
      },
      {
        seriesName: "BPA Ordered",
        show: false,
      },
      {
        seriesName: "Unique BPA Members",
        show: false,
      },
      {
        seriesName: "Total Price",
        opposite: true,
        title: {
          text: "Total Price",
        },
        labels: {
          formatter: function (value) {
            return formatCurrency(value);
          },
        },
      },
    ],
  });

  const getData = (download) => {
    const data = {
      dateFrom:
        dateFrom.current != null
          ? moment(dateFrom.current).format("YYYY-MM-DD")
          : null,
      dateTo:
        dateTo.current != null
          ? moment(dateTo.current).format("YYYY-MM-DD")
          : null,
      download: download,
    };
    if (isGlobalDateFilter && shouldUseMonthlyEndpoint(dateFilter)) {
      dispatch(getMonthlyGeneralCustomerOrderCounts(data));
    } else {
      dispatch(getWeeklyGeneralCustomerOrderCounts(data));
    }
  };

  const periodLabel =
    isGlobalDateFilter && shouldUseMonthlyEndpoint(dateFilter)
      ? "Monthly"
      : "Weekly";

  useEffect(() => {
    if (weeklyGeneralCustomerOrderCounts) {
      const chartItems = toChartItems(weeklyGeneralCustomerOrderCounts);

      setSeries([
        {
          name: "Ordered",
          type: "column",
          data: chartItems.map((item) => toChartNumber(item?.orderedCount)),
          color: "#0AB39C",
        },
        {
          name: "Cancelled",
          type: "column",
          data: chartItems.map((item) => toChartNumber(item?.cancelledCount)),
          color: "#F06548",
        },
        {
          name: "BPA Ordered",
          type: "column",
          data: chartItems.map((item) => toChartNumber(item?.bpaOrderedCount)),
          color: "#405189",
        },
        {
          name: "Unique BPA Members",
          type: "column",
          data: chartItems.map((item) =>
            toChartNumber(item?.bpaUniqueMemberCount)
          ),
          color: "#FFB302",
        },
        {
          name: "Total Price",
          type: "line",
          data: chartItems.map((item) => toChartNumber(item?.totalPrice)),
          color: "#00E396",
        },
      ]);

      setOptions((prevOptions) => ({
        ...prevOptions,
        xaxis: {
          ...prevOptions.xaxis,
          categories: chartItems.map((item) => toChartLabel(item?.label)),
        },
      }));
    }
  }, [weeklyGeneralCustomerOrderCounts]);

  useEffect(() => {
    if (!isGlobalDateFilter) {
      return;
    }

    const range = getDashboardDateRange(
      dateFilter.period,
      dateFilter.customDateFrom,
      dateFilter.customDateTo
    );

    dateFrom.current = range.dateFrom;
    dateTo.current = range.dateTo;

    if (
      dateFilter.period !== DASHBOARD_DATE_FILTER_PERIODS.CUSTOM ||
      range.dateFrom ||
      range.dateTo
    ) {
      getData();
    }
  }, [
    dateFilter?.customDateFrom,
    dateFilter?.customDateTo,
    dateFilter?.period,
    isGlobalDateFilter,
  ]);

  useEffect(() => {
    if (isGlobalDateFilter) {
      return;
    }

    switch (selectedPeriod) {
      case "7 days":
        const date = new Date();
        const sevenDaysAgo = new Date(date);
        sevenDaysAgo.setDate(date.getDate() - 7);
        dateFrom.current = sevenDaysAgo;
        dateTo.current = new Date();
        break;
      case "30 days":
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(new Date().getDate() - 30);
        dateFrom.current = thirtyDaysAgo;
        dateTo.current = new Date();
        break;
      case "all":
        dateFrom.current = null;
        dateTo.current = null;
        break;
      case "custom":
        break;
      default:
        dateFrom.current = null;
        dateTo.current = null;
        break;
    }
    if (selectedPeriod !== "custom") {
      getData();
    }
  }, [isGlobalDateFilter, selectedPeriod]);

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">
            {periodLabel} Total Member Purchases
          </h4>
          {!isGlobalDateFilter && <div className="d-flex gap-1">
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "all" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => {
                setSelectedPeriod("all");
              }}
            >
              ALL
            </button>
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "7 days" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => {
                setSelectedPeriod("7 days");
              }}
            >
              7 Days
            </button>
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "30 days" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => {
                setSelectedPeriod("30 days");
              }}
            >
              30 Days
            </button>
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "custom" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => {
                setSelectedPeriod("custom");
              }}
            >
              Custom
            </button>
            {selectedPeriod === "custom" && (
              <div className="d-flex align-items-center gap-2 mt-2">
                <Flatpickr
                  className="form-control"
                  id="datepicker-publish-input"
                  placeholder="Select a date from"
                  onChange={(selectedDates, dateStr, instance) => {
                    dateFrom.current = selectedDates[0];
                    getData();
                  }}
                  options={{
                    maxDate: dateTo.current,
                    mode: "single",
                    dateFormat: "m-d-Y",
                  }}
                />
                <span>to</span>
                <Flatpickr
                  className="form-control"
                  id="datepicker-publish-input"
                  placeholder="Select a date to"
                  onChange={(selectedDates, dateStr, instance) => {
                    dateTo.current = selectedDates[0];
                    getData();
                  }}
                  options={{
                    mode: "single",
                    minDate: dateFrom.current,
                    dateFormat: "m-d-Y",
                  }}
                />
              </div>
            )}
          </div>}
        </CardHeader>
        <CardBody className="p-0 pb-2">
          <div className="w-100">
            <div dir="ltr">
              {weeklyGeneralCustomerOrderCounts ? (
                <ReactApexChart
                  ref={chart}
                  options={options}
                  series={series}
                  type="line"
                  height={350}
                />
              ) : (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default WeeklyTotalMemberPurchases;
